import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useTags } from 'features/tags';
import {
  useArchiveTask,
  useCompleteTask,
  useCreateHabitAllowed,
  useCreateTask,
  useUpdateTask,
} from 'features/tasks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOpenDeleteHabitDialog } from 'shared/contexts/delete-habit';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { HabitFormDialog } from 'shared/dialogs/habit-form';
import { useUpdateGoalTaskCountMutation } from 'shared/hooks/queries/use-update-goal-task-count-mutation';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useRelationshipIdsById } from 'shared/hooks/use-relationships';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { useUser } from 'shared/hooks/use-user';
import { initialDateFormat } from 'shared/types/date-format-options';
import { Habit, NewHabit } from 'shared/types/habit';
import { HabitFormFields } from 'shared/types/habit-form';
import { ID, IDPrefixes } from 'shared/types/id';
import { LimitKeys } from 'shared/types/limit-keys';
import { RepeatingTask } from 'shared/types/repeating-task';
import { TaskType } from 'shared/types/task-base';
import { getTimeFormat } from 'shared/utils/get-time-format';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { createPrefixId } from 'shared/utils/prefix-id';

import { HabitFormContext, HabitFormContextProps } from './habit-form-context';

export type HabitFormProviderProps = {
  children: React.ReactNode;
};

export const HabitFormProvider: React.FC<HabitFormProviderProps> = ({
  children,
}) => {
  const [habitId, setHabitId] = useState<ID>();
  const [habit, setHabit] = useState<Habit | RepeatingTask>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [initialValues, setInitialValues] =
    useState<Partial<HabitFormFields>>();

  const user = useUser();
  const { allowed: customIconAllowed } = usePremiumFeatureAllowed(
    LimitKeys.HabitIcons,
    {},
  );
  const { allowed: createHabitAllowed } = useCreateHabitAllowed();
  const { allowed: scheduleTargetAllowed } = usePremiumFeatureAllowed(
    LimitKeys.HabitScheduleTarget,
    {},
  );
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const tags = useTags();
  const { tag: initialValuesTags } = useRelationshipIdsById(
    habitId,
    IDPrefixes.Task,
  );

  // useOpenPremiumDialog is part of a higher level context which should be kept in mind (probably different setup needed)
  const openPremiumDialog = useOpenPremiumDialog();
  const { mutate: updateGoalTaskCount } = useUpdateGoalTaskCountMutation();

  const create = useCreateTask();
  const update = useUpdateTask();
  const updateRelationships = useUpdateRelationshipsReferences();
  const completeHabit = useCompleteTask();
  const archiveHabit = useArchiveTask();
  const deleteHabit = useOpenDeleteHabitDialog();
  const { retry, isLoading, error, isSuccess, reset } = useMemo(
    () => (habitId ? update : create),
    [create, habitId, update],
  );

  const openDialog = useCallback(
    (initialValues?: Partial<HabitFormFields>, id?: ID) => {
      setHabitId(id);
      setInitialValues(initialValues);
      setDialogOpen(true);
    },
    [setDialogOpen],
  );
  const openDialogWithHabit = useCallback(
    (habit: Habit | RepeatingTask) => {
      setHabitId(habit.id);
      setHabit(habit);
      setInitialValues(habit);
      setDialogOpen(true);
    },
    [setDialogOpen],
  );

  const closeDialog = useCallback(() => {
    reset();
    setInitialValues(undefined);
    setHabitId(undefined);
    setHabit(undefined);
    setDialogOpen(false);
  }, [reset]);

  const submitForm = useCallback(
    async ({ tagIds, ...values }: HabitFormFields) => {
      if (!values.name) {
        return;
      }

      // update goal task count when the goalId is updated
      if (values.goalId !== habit?.goalId) {
        values.goalId &&
          updateGoalTaskCount({
            goalId: values.goalId,
            taskCount: 1,
            completedTaskCount: habit?.completedAt ? 1 : 0,
          });
        habit?.goalId &&
          updateGoalTaskCount({
            goalId: habit.goalId,
            taskCount: -1,
            completedTaskCount: habit.completedAt ? -1 : 0,
          });
      }

      if (habitId) {
        update.submit({ ...values, id: habitId } as Habit);
        const tagsToRemove =
          initialValuesTags?.filter((tagId) => !tagIds?.includes(tagId)) ?? [];
        const tagsToAdd =
          tagIds?.filter((tagId) => !initialValuesTags?.includes(tagId)) ?? [];
        (tagsToRemove.length || tagsToAdd.length) &&
          updateRelationships([
            ...tagsToAdd.map((tagId) => ({
              id: createPrefixId(tagId, IDPrefixes.Tag),
              add: [createPrefixId(habitId, IDPrefixes.Task)],
            })),
            ...tagsToRemove.map((tagId) => ({
              id: createPrefixId(tagId, IDPrefixes.Tag),
              remove: [createPrefixId(habitId, IDPrefixes.Task)],
            })),
          ]);
        return;
      }

      const { id } = await create.submit(values as unknown as NewHabit);
      tagIds &&
        updateRelationships(
          tagIds.map((tagId) => ({
            id: createPrefixId(tagId, IDPrefixes.Tag),
            add: [createPrefixId(id, IDPrefixes.Task)],
          })),
        );
    },
    [
      create,
      habit?.completedAt,
      habit?.goalId,
      habitId,
      initialValuesTags,
      update,
      updateGoalTaskCount,
      updateRelationships,
    ],
  );

  const onComplete = () => {
    if (!habit) {
      return;
    }
    completeHabit(habit);
    closeDialog();
  };

  const onArchive = () => {
    if (!habit) {
      return;
    }
    archiveHabit(habit);
    habit.goalId &&
      updateGoalTaskCount({
        goalId: habit.goalId,
        taskCount: -1,
        completedTaskCount: habit.completedAt ? -1 : 0,
      });
    closeDialog();
  };

  const onDelete = () => {
    if (!habit) {
      return;
    }

    deleteHabit(habit as Habit);
    closeDialog();
  };

  const _initialValues = useMemo(
    () => ({
      ...initialValues,
      tagIds: initialValuesTags,
    }),
    [initialValues, initialValuesTags],
  );

  const value = useMemo<HabitFormContextProps>(
    () => ({
      onCreateHabit: openDialog,
      onEditHabit: openDialogWithHabit,
    }),
    [openDialog, openDialogWithHabit],
  );

  useEffect(() => {
    if (isSuccess) {
      closeDialog();
    }
  }, [closeDialog, isSuccess]);

  return (
    <HabitFormContext.Provider value={value}>
      {children}
      <HabitFormDialog
        open={dialogOpen}
        initialValues={_initialValues}
        goals={goals}
        lifeAreas={lifeAreas}
        tags={tags}
        dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
        weekStartsOn={getWeekStartsOn(user)}
        timeFormat={getTimeFormat(user)}
        onClose={closeDialog}
        onSubmit={submitForm}
        onRetry={retry}
        isLoading={isLoading}
        isError={!!error}
        isEdit={!!habitId}
        isCustomIconAllowed={customIconAllowed}
        isRepeatTargetAllowed={scheduleTargetAllowed}
        isHabitCreationAllowed={
          createHabitAllowed || habit?.type === TaskType.Habit
        }
        onPremium={openPremiumDialog}
        isCompleted={!!habit?.completedAt}
        isArchived={!!habit?.archivedAt}
        onComplete={habit ? onComplete : undefined}
        onArchive={habit ? onArchive : undefined}
        onDelete={habit ? onDelete : undefined}
      />
    </HabitFormContext.Provider>
  );
};
